<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <div class="d-flex align-center mb-4">
          <v-icon left color="warning" class="mr-4" large>mdi-database-sync</v-icon>
          <div>
            <h1 class="text-left">User Data Migration</h1>
            <p class="text--secondary mb-0">Simplify user data to essential fields only - removes ALL legacy fields and creates clean structure</p>
          </div>
        </div>

        <!-- Migration Status -->
        <v-row class="mb-4">
          <v-col cols="3">
            <v-card color="blue lighten-4">
              <v-card-text class="text-center">
                <div class="text-h4">{{ migrationStats.total }}</div>
                <div>Total Users</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="3">
            <v-card color="orange lighten-4">
              <v-card-text class="text-center">
                <div class="text-h4">{{ migrationStats.needsMigration }}</div>
                <div>Needs Migration</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="3">
            <v-card color="green lighten-4">
              <v-card-text class="text-center">
                <div class="text-h4">{{ migrationStats.migrated }}</div>
                <div>Already Migrated</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="3">
            <v-card color="purple lighten-4">
              <v-card-text class="text-center">
                <div class="text-h4">{{ migrationStats.errors }}</div>
                <div>Errors</div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Migration Controls -->
        <v-card class="mb-4">
          <v-card-title>
            Migration Controls
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="6">
                <v-btn 
                  color="primary" 
                  @click="analyzeUsers" 
                  :loading="analyzing"
                  block
                >
                  <v-icon left>mdi-magnify</v-icon>
                  Analyze Users
                </v-btn>
              </v-col>
              <v-col cols="6">
                <v-btn 
                  color="success" 
                  @click="startMigration" 
                  :loading="migrating"
                  :disabled="!migrationData.length || migrating"
                  block
                >
                  <v-icon left>mdi-database-export</v-icon>
                  Migrate All Users
                </v-btn>
              </v-col>
            </v-row>
            <v-row class="mt-2">
              <v-col cols="6">
                <v-btn 
                  color="warning" 
                  @click="previewMigration" 
                  :disabled="!migrationData.length"
                  block
                  outlined
                >
                  <v-icon left>mdi-eye</v-icon>
                  Preview Changes
                </v-btn>
              </v-col>
              <v-col cols="6">
                <v-btn 
                  color="error" 
                  @click="cleanupOldFields" 
                  :loading="cleaning"
                  block
                  outlined
                >
                  <v-icon left>mdi-broom</v-icon>
                  Remove Legacy Fields
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Migration Progress -->
        <v-card class="mb-4" v-if="migrationProgress.show">
          <v-card-title>Migration Progress</v-card-title>
          <v-card-text>
            <v-progress-linear 
              :value="migrationProgress.percentage" 
              height="25"
              reactive
              striped
            >
              <span class="white--text">{{ migrationProgress.current }}/{{ migrationProgress.total }}</span>
            </v-progress-linear>
            <div class="mt-2">
              <strong>Current:</strong> {{ migrationProgress.currentUser }}
            </div>
          </v-card-text>
        </v-card>

        <!-- Users to Migrate Table -->
        <v-card>
          <v-card-title>
            Users Requiring Migration
            <v-spacer></v-spacer>
            <v-text-field
              v-model="userSearch"
              append-icon="mdi-magnify"
              label="Search users..."
              single-line
              hide-details
              class="mr-4"
              style="max-width: 300px;"
            ></v-text-field>
          </v-card-title>
          
          <v-card-text>
            <v-data-table
              :headers="migrationHeaders"
              :items="migrationData"
              :search="userSearch"
              :loading="loadingUsers"
              class="elevation-1"
            >
              <template v-slot:item.issues="{ item }">
                <v-chip
                  v-for="issue in item.issues"
                  :key="issue"
                  small
                  color="warning"
                  class="mr-1 mb-1"
                >
                  {{ issue }}
                </v-chip>
              </template>

              <template v-slot:item.proposed_changes="{ item }">
                <v-btn
                  small
                  color="info"
                  @click="viewChanges(item)"
                >
                  <v-icon small left>mdi-eye</v-icon>
                  View
                </v-btn>
              </template>

              <template v-slot:item.actions="{ item }">
                <v-btn
                  small
                  color="success"
                  @click="migrateUser(item)"
                  :loading="item.migrating"
                  :disabled="item.migrating"
                >
                  <v-icon small left>mdi-database-export</v-icon>
                  Migrate
                </v-btn>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Migration Preview Dialog -->
    <v-dialog v-model="showPreviewDialog" max-width="900px">
      <v-card v-if="previewUser">
        <v-card-title>Migration Preview: {{ previewUser.email }}</v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="6">
              <h3>Before Migration</h3>
              <pre class="migration-preview">{{ JSON.stringify(previewUser.before, null, 2) }}</pre>
            </v-col>
            <v-col cols="6">
              <h3>After Migration</h3>
              <pre class="migration-preview">{{ JSON.stringify(previewUser.after, null, 2) }}</pre>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showPreviewDialog = false">Close</v-btn>
          <v-btn color="success" @click="migrateUser(previewUser); showPreviewDialog = false">
            Migrate This User
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Migration Results Dialog -->
    <v-dialog v-model="showResultsDialog" max-width="600px">
      <v-card>
        <v-card-title>Migration Results</v-card-title>
        <v-card-text>
          <div v-for="result in migrationResults" :key="result.email" class="mb-2">
            <v-row align="center">
              <v-col cols="8">
                {{ result.email }}
              </v-col>
              <v-col cols="4">
                <v-chip :color="result.success ? 'green' : 'red'" small>
                  {{ result.success ? 'Success' : 'Failed' }}
                </v-chip>
              </v-col>
            </v-row>
            <div v-if="!result.success" class="error--text text-caption">
              {{ result.error }}
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showResultsDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Success/Error Snackbars -->
    <v-snackbar v-model="showSnackbar" :color="snackbarColor" timeout="4000">
      {{ snackbarMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="showSnackbar = false">Close</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import { getFirestore, collection, getDocs, doc, updateDoc, deleteField, setDoc } from 'firebase/firestore'
import { getAllUsers } from '@/utils/userManagement'
import { getAllGroups, getDivisions, getToolsForGroup } from '@/config/toolsConfig'

export default {
  name: 'UserMigration',
  props: ['user'],
  
  data() {
    return {
      // Migration data
      allUsers: [],
      migrationData: [],
      loadingUsers: false,
      analyzing: false,
      migrating: false,
      cleaning: false,
      userSearch: '',
      
      // Statistics
      migrationStats: {
        total: 0,
        needsMigration: 0,
        migrated: 0,
        errors: 0
      },
      
      // Progress tracking
      migrationProgress: {
        show: false,
        current: 0,
        total: 0,
        percentage: 0,
        currentUser: ''
      },
      
      // Dialogs
      showPreviewDialog: false,
      showResultsDialog: false,
      previewUser: null,
      migrationResults: [],
      
      // UI
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success',
      
      // Valid groups and divisions from config
      validGroups: getAllGroups().map(g => g.id),
      validDivisions: getDivisions().map(d => d.id),
      
      // Table headers
      migrationHeaders: [
        { text: 'Email', value: 'email' },
        { text: 'Current Status', value: 'status' },
        { text: 'Issues Found', value: 'issues' },
        { text: 'Proposed Changes', value: 'proposed_changes', sortable: false },
        { text: 'Actions', value: 'actions', sortable: false }
      ]
    }
  },
  
  async created() {
    await this.loadAllUsers()
    await this.analyzeUsers() // Automatically run analysis on page load
  },
  
  methods: {
    async loadAllUsers() {
      this.loadingUsers = true
      try {
        this.allUsers = await getAllUsers()
        this.calculateStats()
      } catch (error) {
        this.showError('Error loading users: ' + error.message)
      }
      this.loadingUsers = false
    },
    
    calculateStats() {
      this.migrationStats = {
        total: this.allUsers.length,
        needsMigration: this.migrationData.length,
        migrated: this.allUsers.filter(u => this.isUserMigrated(u)).length,
        errors: 0
      }
    },
    
    isUserMigrated(user) {
       // User needs migration if they have ANY legacy fields that need cleanup
       // This is more aggressive - if ANY legacy junk exists, flag for cleanup
       
       // List of ALL known legacy/junk fields that should be removed
       const legacyFields = [
         'roles', 'department', 'status', 'division', 'group', 'manager_status',
         'displayName', 'photoURL', 'emailVerified', 'phoneNumber', 
         'metadata', 'customClaims', 'providerData', 'reloadUserInfo',
         'stsTokenManager', 'accessToken', 'refreshToken', 'expirationTime',
         'uid_', 'auth', 'isAnonymous', 'tenantId', 'apiKey', 'appName',
         'multiFactor', 'claims'
       ]
       
       // Check for any legacy fields
       const hasLegacyFields = legacyFields.some(field => user.hasOwnProperty(field))
       
       // Check for string dates (should be Date objects)
       const hasStringDates = (
         (user.invited_at && typeof user.invited_at === 'string') ||
         (user.created && typeof user.created === 'string') ||
         (user.last_login && typeof user.last_login === 'string')
       )
       
       // Check for old array-based roles
       const hasOldRoleStructure = Array.isArray(user.roles)
       
       // Check for single division/group instead of arrays
       const hasOldStructure = (
         user.division && !Array.isArray(user.divisions)
       ) || (
         user.group && !Array.isArray(user.groups)
       )
       
       // User is NOT migrated if they have ANY of these issues
       const needsMigration = hasLegacyFields || hasStringDates || hasOldRoleStructure || hasOldStructure
       
       return !needsMigration // Return true only if NO migration needed
     },
    
    async analyzeUsers() {
      this.analyzing = true
      this.migrationData = []
      
      try {
        for (const user of this.allUsers) {
          const issues = this.findUserIssues(user)
          if (issues.length > 0) {
            this.migrationData.push({
              ...user,
              issues,
              migrating: false,
              before: { ...user },
              after: this.generateMigratedUser(user)
            })
          }
        }
        
        this.calculateStats()
        this.showSuccess(`Analysis complete. Found ${this.migrationData.length} users requiring migration.`)
      } catch (error) {
        this.showError('Error analyzing users: ' + error.message)
      }
      
      this.analyzing = false
    },
    
    findUserIssues(user) {
      const issues = []
      
      // Legacy structural issues are now covered in the legacy fields check above
      
      if (!user.role && user.role !== '') {
        issues.push('Missing role field')
      }
      
      if (user.division && !Array.isArray(user.divisions)) {
        issues.push('Single division field (needs array)')
      }
      
      if (user.group && !Array.isArray(user.groups)) {
        issues.push('Single group field (needs array)')
      }
      
      // Check for ALL legacy/junk fields that need removal
      const legacyFields = [
        'roles', 'department', 'status', 'division', 'group', 'manager_status',
        'displayName', 'photoURL', 'emailVerified', 'phoneNumber', 
        'metadata', 'customClaims', 'providerData', 'reloadUserInfo',
        'stsTokenManager', 'accessToken', 'refreshToken', 'expirationTime',
        'uid_', 'auth', 'isAnonymous', 'tenantId', 'apiKey', 'appName',
        'multiFactor', 'claims'
      ]
      
      const foundLegacyFields = legacyFields.filter(field => user.hasOwnProperty(field))
      
      if (foundLegacyFields.length > 0) {
        issues.push(`Legacy junk fields: ${foundLegacyFields.join(', ')}`)
      }
      
      // Check for string dates instead of timestamps
      const stringDateFields = []
      if (user.invited_at && typeof user.invited_at === 'string') stringDateFields.push('invited_at')
      if (user.created && typeof user.created === 'string') stringDateFields.push('created')
      if (user.last_login && typeof user.last_login === 'string') stringDateFields.push('last_login')
      
      if (stringDateFields.length > 0) {
        issues.push(`String dates: ${stringDateFields.join(', ')}`)
      }
      
      // Check for missing required structure
      if (!Array.isArray(user.divisions)) {
        issues.push('Missing divisions array')
      }
      
      if (!Array.isArray(user.groups)) {
        issues.push('Missing groups array')
      }
      
      if (!user.tool_permissions || typeof user.tool_permissions !== 'object') {
        issues.push('Missing tool_permissions object')
      }
      
      // Role field check is handled above
      
      return issues
    },
    
    generateMigratedUser(user) {
      // Create completely new simplified user object with only essential fields
      const migrated = {
        // Core identification
        email: user.email,
        uid: user.uid || null,
        
        // New role system
        role: this.deriveRole(user),
        divisions: this.deriveDivisions(user),
        groups: this.deriveGroups(user),
        tool_permissions: user.tool_permissions || {},
        
        // Profile information
        profile: {
          name: user.profile?.name || user.displayName || '',
          photo: user.profile?.photo || user.photoURL || '',
          emailVerified: user.profile?.emailVerified || user.emailVerified || false
        },
        
        // Metadata (preserve essential tracking info)
        invited_by: user.invited_by || 'system',
        invited_at: user.invited_at || new Date(),
        created: user.created || user.invited_at || new Date(),
        last_login: user.last_login || null
      }
      
      return migrated
    },
    
    deriveRole(user) {
      // Convert old roles array to single role, or ensure user has a role
      if (Array.isArray(user.roles)) {
        const roleHierarchy = ['', 'Employee', 'Pioneer', 'Manager', 'Admin']
        const userRoles = user.roles || []
        let highestRole = 'Employee' // Default
        
        for (const role of roleHierarchy) {
          if (userRoles.includes(role)) {
            highestRole = role
          }
        }
        return highestRole
      } else if (user.role && user.role !== '') {
        return user.role
      } else {
        return 'Employee' // Default role for users without one
      }
    },
    
    deriveDivisions(user) {
      // Convert single division to array or use existing array
      if (Array.isArray(user.divisions)) {
        return user.divisions
      } else if (user.division) {
        return [user.division]
      } else if (user.department) {
        // Map old department to division if possible
        return [user.department]
      } else {
        return ['corporate_planning'] // Default division
      }
    },
    
    deriveGroups(user) {
      // Convert single group to array or use existing array
      if (Array.isArray(user.groups)) {
        return user.groups
      } else if (user.group) {
        return [user.group]
      } else {
        return ['corporate_planning'] // Default group
      }
    },
    
    getToolsForGroups(groups) {
      // Get all tools available for the given groups
      const allTools = []
      groups.forEach(groupId => {
        const tools = getToolsForGroup(groupId)
        tools.forEach(tool => {
          // Avoid duplicates
          if (!allTools.find(t => t.id === tool.id)) {
            allTools.push(tool)
          }
        })
      })
      return allTools
    },
    
    async startMigration() {
      this.migrating = true
      this.migrationProgress.show = true
      this.migrationProgress.current = 0
      this.migrationProgress.total = this.migrationData.length
      this.migrationResults = []
      
      try {
        for (let i = 0; i < this.migrationData.length; i++) {
          const userData = this.migrationData[i]
          this.migrationProgress.current = i + 1
          this.migrationProgress.percentage = ((i + 1) / this.migrationData.length) * 100
          this.migrationProgress.currentUser = userData.email
          
          try {
            await this.migrateUserData(userData)
            this.migrationResults.push({
              email: userData.email,
              success: true
            })
          } catch (error) {
            this.migrationResults.push({
              email: userData.email,
              success: false,
              error: error.message
            })
          }
          
          // Small delay to prevent overwhelming Firestore
          await new Promise(resolve => setTimeout(resolve, 100))
        }
        
        this.showResultsDialog = true
        this.showSuccess('Migration completed!')
        await this.loadAllUsers()
        await this.analyzeUsers()
        
      } catch (error) {
        this.showError('Migration failed: ' + error.message)
      }
      
      this.migrating = false
      this.migrationProgress.show = false
    },
    
    async migrateUser(userData) {
      userData.migrating = true
      
      try {
        await this.migrateUserData(userData)
        this.showSuccess(`Successfully migrated ${userData.email}`)
        
        // Remove from migration list
        const index = this.migrationData.findIndex(u => u.email === userData.email)
        if (index > -1) {
          this.migrationData.splice(index, 1)
        }
        
        this.calculateStats()
      } catch (error) {
        this.showError(`Failed to migrate ${userData.email}: ${error.message}`)
      }
      
      userData.migrating = false
    },
    
    async migrateUserData(userData) {
      const db = getFirestore()
      const userRef = doc(db, 'users', userData.email)
      
      // Generate the complete new simplified user structure
      const migratedUser = this.generateMigratedUser(userData)
      
      // Convert date fields to proper Firestore format
      if (migratedUser.invited_at && typeof migratedUser.invited_at === 'string') {
        migratedUser.invited_at = new Date(migratedUser.invited_at)
      }
      
      if (migratedUser.created && typeof migratedUser.created === 'string') {
        migratedUser.created = new Date(migratedUser.created)
      }
      
      if (migratedUser.last_login && typeof migratedUser.last_login === 'string') {
        migratedUser.last_login = new Date(migratedUser.last_login)
      }
      
      // Auto-generate manager_status based on role for Managers
      if (migratedUser.role === 'Manager' && migratedUser.groups) {
        migratedUser.manager_status = {}
        migratedUser.groups.forEach(groupId => {
          migratedUser.manager_status[groupId] = true
        })
      }
      
      // Auto-generate tool permissions for Managers
      if (migratedUser.role === 'Manager' && migratedUser.groups) {
        // Managers get access to all tools for their groups
        const tools = this.getToolsForGroups(migratedUser.groups)
        tools.forEach(tool => {
          if (!migratedUser.tool_permissions[tool.id]) {
            migratedUser.tool_permissions[tool.id] = { access: true }
          }
        })
      }
      
      // Completely replace the document with the clean structure
      // This removes ALL legacy fields not included in migratedUser
      await setDoc(userRef, migratedUser)
    },
    
    async cleanupOldFields() {
      this.cleaning = true
      
      try {
        const db = getFirestore()
        let cleaned = 0
        
        // List of all known legacy fields to remove
        const legacyFields = [
          'department', 'status', 'roles', 'division', 'group', 'manager_status',
          'displayName', 'photoURL', 'emailVerified', 'phoneNumber', 
          'metadata', 'customClaims', 'providerData', 'reloadUserInfo',
          'stsTokenManager', 'accessToken', 'refreshToken', 'expirationTime'
        ]
        
        for (const user of this.allUsers) {
          const fieldsToDelete = {}
          
          // Check for any legacy fields and mark for deletion
          legacyFields.forEach(field => {
            if (user.hasOwnProperty(field)) {
              fieldsToDelete[field] = deleteField()
            }
          })
          
          if (Object.keys(fieldsToDelete).length > 0) {
            const userRef = doc(db, 'users', user.email)
            await updateDoc(userRef, fieldsToDelete)
            cleaned++
          }
        }
        
        this.showSuccess(`Cleaned up legacy fields from ${cleaned} users`)
        await this.loadAllUsers()
        await this.analyzeUsers()
        
      } catch (error) {
        this.showError('Cleanup failed: ' + error.message)
      }
      
      this.cleaning = false
    },
    
    previewMigration() {
      if (this.migrationData.length > 0) {
        this.viewChanges(this.migrationData[0])
      }
    },
    
    viewChanges(userData) {
      this.previewUser = userData
      this.showPreviewDialog = true
    },
    
    showSuccess(message) {
      this.snackbarMessage = message
      this.snackbarColor = 'success'
      this.showSnackbar = true
    },
    
    showError(message) {
      this.snackbarMessage = message
      this.snackbarColor = 'error'
      this.showSnackbar = true
    }
  }
}
</script>

<style scoped>
.migration-preview {
  background-color: #f5f5f5;
  padding: 16px;
  border-radius: 4px;
  font-size: 12px;
  max-height: 400px;
  overflow-y: auto;
  white-space: pre-wrap;
}
</style> 